/**
 * PlanTypesAdmin.tsx — Admin > Plan Types.
 *
 * The corporate dashboard's Plan Types page for the signed-in account, in the
 * Lead Sources layout (see components/admin/AdminList.tsx):
 *   - Plan Type Name, Pipeline ("Default" when none) and Restrict Quote (corp's
 *     Yes / No outline badges), in the account's order until a title is
 *     clicked; search by name (debounced, as corp); Show deleted;
 *   - "Add plan type" top right; a click on a live row opens it; Edit / Delete
 *     on a live row, Restore on a deleted one;
 *   - corp's form: Name *, Pipeline Type (Default, or one of the account's live
 *     pipelines) and "Restrict to One Active Quote".
 * Delete sets the type inactive; Restore sets it active again.
 * On a Locked page (useAdminPageLevel) there is no Add, no row icon and no
 * Actions column; a live type still opens, read-only.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import {
  ACTIONS_COLUMN,
  ADMIN_TD_CLASS,
  AdminListEmpty,
  AdminListPage,
  AdminListTable,
  AdminListToolbar,
  AdminRowName,
  PaginationFooter,
  adminRowClass,
  type AdminListColumn,
} from '@/components/admin/AdminList';
import { AdminFormModal } from '@/components/admin/AdminFormModal';
import { AdminRowAction, AdminRowActions } from '@/components/admin/AdminRowActions';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { OutlineBadge } from '@/components/admin/CorpBadges';
import { DialogField, SwitchField, fieldClasses } from '@/components/admin/DialogChrome';
import { useListPagination } from '@/components/admin/useListPagination';
import { compareText, useListSort, type ListSortCompare } from '@/components/admin/useListSort';
import { useTrackedForm } from '@/components/admin/useTrackedForm';
import { useDebounce } from '@/hooks/useDebounce';
import { getErrorMessage } from '@/services/apiClient';
import {
  createPlanType,
  deletePlanType,
  listAdminPlanTypes,
  listPipelineTypeOptions,
  restorePlanType,
  updatePlanType,
  type AdminPlanType,
  type PipelineTypeOption,
} from '@/services/planTypes.service';
import { toast } from '@/store/toastStore';

const SEARCH_DEBOUNCE_MS = 400;

const COLUMNS: AdminListColumn[] = [
  { key: 'name', header: 'Plan Type Name', sortable: true },
  { key: 'pipeline', header: 'Pipeline', sortable: true },
  { key: 'restrict', header: 'Restrict Quote', className: 'w-36' },
  ACTIONS_COLUMN,
];

/** A Locked page has no row actions, so no Actions column. */
const LOCKED_COLUMNS = COLUMNS.filter((c) => c !== ACTIONS_COLUMN);

function typeName(p: AdminPlanType): string {
  return p.name.trim() || `Plan type #${p.planTypeID}`;
}

function pipelineName(p: AdminPlanType): string {
  return p.pipelineTypeID > 0 && p.pipelineTitle.trim() ? p.pipelineTitle : 'Default';
}

const SORT_COMPARE: ListSortCompare<AdminPlanType> = {
  name: (a, b) => compareText(typeName(a), typeName(b)),
  pipeline: (a, b) => compareText(pipelineName(a), pipelineName(b)),
};

export function PlanTypesAdmin() {
  const locked = useAdminPageLevel() === 'locked';
  const [types, setTypes] = useState<AdminPlanType[]>([]);
  const [pipelines, setPipelines] = useState<PipelineTypeOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [editing, setEditing] = useState<AdminPlanType | 'new' | null>(null);
  const [deleting, setDeleting] = useState<AdminPlanType | null>(null);
  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      setTypes(await listAdminPlanTypes());
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to load plan types'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    listPipelineTypeOptions()
      .then(setPipelines)
      .catch(() => setPipelines([]));
  }, [load]);

  const query = debouncedSearch.trim().toLowerCase();
  const rows = useMemo(
    () => types.filter((p) => (showDeleted || p.active) && (query === '' || p.name.toLowerCase().includes(query))),
    [types, showDeleted, query],
  );
  const sorting = useListSort(rows, { initialKey: null, compare: SORT_COMPARE });
  const pagination = useListPagination(sorting.sorted, { resetOn: [query, showDeleted] });

  async function handleDelete() {
    if (!deleting) return;
    const { planTypeID } = deleting;
    try {
      await deletePlanType(planTypeID);
      setTypes((prev) => prev.map((p) => (p.planTypeID === planTypeID ? { ...p, active: false } : p)));
      toast.success('Plan type deleted');
    } catch (err) {
      toast.error(getErrorMessage(err, 'The plan type could not be deleted.'));
    }
  }

  async function handleRestore(p: AdminPlanType) {
    try {
      await restorePlanType(p.planTypeID);
      setTypes((prev) => prev.map((x) => (x.planTypeID === p.planTypeID ? { ...x, active: true } : x)));
      toast.success('Plan type restored');
    } catch (err) {
      toast.error(getErrorMessage(err, 'The plan type could not be restored.'));
    }
  }

  return (
    <AdminListPage
      title="Plan Types"
      subtitle="The kinds of plans the account tracks, and the pipeline each one follows."
      actions={
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setEditing('new')}>
          Add plan type
        </Button>
      }
      toolbar={
        <AdminListToolbar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search plan types..."
          searchLabel="Search plan types"
          showDeleted={showDeleted}
          onShowDeletedChange={setShowDeleted}
          showDeletedId="plan-types-show-deleted"
        />
      }
    >
      {loading ? (
        <SpinnerOverlay label="Loading plan types…" />
      ) : rows.length === 0 ? (
        <AdminListEmpty
          title={query ? 'No plan types found' : 'No plan types yet'}
          hint={query ? 'Try adjusting your search.' : locked ? undefined : 'Add a plan type to get started.'}
        />
      ) : (
        <>
          <AdminListTable columns={locked ? LOCKED_COLUMNS : COLUMNS} aria-label="Plan types" sort={sorting.sort} onSortChange={sorting.onSortChange}>
            {pagination.pageItems.map((p) => {
              const deleted = !p.active;
              const name = typeName(p);
              return (
                <tr
                  key={p.planTypeID}
                  className={adminRowClass({ deleted, clickable: !deleted })}
                  onClick={deleted ? undefined : () => setEditing(p)}
                >
                  <td className={ADMIN_TD_CLASS}>
                    <AdminRowName deleted={deleted} nameTestId="plan-type-name">
                      {name}
                    </AdminRowName>
                  </td>
                  <td className={`${ADMIN_TD_CLASS} text-slate-600`}>{pipelineName(p)}</td>
                  <td className={ADMIN_TD_CLASS}>
                    {p.restrictActiveQuote ? <OutlineBadge tone="blue">Yes</OutlineBadge> : <OutlineBadge tone="gray">No</OutlineBadge>}
                  </td>
                  {!locked && (
                    <td className={ADMIN_TD_CLASS}>
                      <AdminRowActions>
                        {deleted ? (
                          <AdminRowAction kind="restore" label={`Restore ${name}`} onClick={() => void handleRestore(p)} />
                        ) : (
                          <>
                            <AdminRowAction kind="edit" label={`Edit ${name}`} onClick={() => setEditing(p)} />
                            <AdminRowAction kind="delete" label={`Delete ${name}`} onClick={() => setDeleting(p)} />
                          </>
                        )}
                      </AdminRowActions>
                    </td>
                  )}
                </tr>
              );
            })}
          </AdminListTable>
          <PaginationFooter pagination={pagination} noun={['plan type', 'plan types']} />
        </>
      )}

      {editing && (
        <PlanTypeFormModal
          key={editing === 'new' ? 'new' : editing.planTypeID}
          planType={editing === 'new' ? null : editing}
          pipelines={pipelines}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load(true);
          }}
        />
      )}

      <ConfirmDialog
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title="Delete plan type?"
        message={deleting ? `${typeName(deleting)} will be deleted. You can restore it from Show deleted.` : ''}
        confirmLabel="Delete plan type"
        variant="danger"
      />
    </AdminListPage>
  );
}

function PlanTypeFormModal({
  planType,
  pipelines,
  onClose,
  onSaved,
}: {
  planType: AdminPlanType | null;
  pipelines: PipelineTypeOption[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = planType === null;
  const t = useTrackedForm(
    () => ({
      name: planType?.name ?? '',
      pipelineTypeID: planType?.pipelineTypeID ?? 0,
      restrictActiveQuote: planType?.restrictActiveQuote ?? false,
    }),
    { tracking: !isNew },
  );
  const [saving, setSaving] = useState(false);
  const missingName = t.form.name.trim() === '';

  const pipelineOptions = useMemo(() => {
    const out = [{ value: 0, label: 'Default' }, ...pipelines.map((p) => ({ value: p.pipelineTypeID, label: p.pipelineTitle }))];
    // A pipeline since retired stays visible on the type that still uses it.
    if (planType && planType.pipelineTypeID > 0 && !out.some((o) => o.value === planType.pipelineTypeID)) {
      out.push({ value: planType.pipelineTypeID, label: planType.pipelineTitle || `Pipeline #${planType.pipelineTypeID}` });
    }
    return out;
  }, [pipelines, planType]);

  async function save() {
    if (missingName) return;
    setSaving(true);
    try {
      const data = { name: t.form.name.trim(), pipelineTypeID: t.form.pipelineTypeID, restrictActiveQuote: t.form.restrictActiveQuote };
      if (planType) await updatePlanType(planType.planTypeID, data);
      else await createPlanType(data);
      toast.success(isNew ? 'Plan type created' : 'Plan type updated');
      onSaved();
    } catch (err) {
      toast.error(getErrorMessage(err, 'The plan type could not be saved.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminFormModal
      title={isNew ? 'Add Plan Type' : 'Edit Plan Type'}
      description={isNew ? 'Create a new plan type.' : undefined}
      isNew={isNew}
      dirty={isNew ? t.form.name !== '' || t.changed.size > 0 : t.dirty}
      saving={saving}
      errors={missingName ? ['Name is required'] : []}
      saveLabel={isNew ? 'Create' : 'Save'}
      onSave={() => void save()}
      onRevertAll={t.revertAll}
      onClose={onClose}
      size="md"
    >
      <div className="grid gap-4">
        <DialogField
          label="Name"
          htmlFor="plan-type-name"
          required
          state={missingName ? 'error' : 'none'}
          labelHint={missingName ? '— required' : undefined}
          badge={t.badge('name', 'Name')}
        >
          <Input
            id="plan-type-name"
            value={t.form.name}
            placeholder="Plan type name"
            maxLength={255}
            onChange={(e) => t.patch({ name: e.target.value })}
            className={fieldClasses.input(missingName ? 'error' : 'none')}
          />
        </DialogField>
        <DialogField label="Pipeline Type" htmlFor="plan-type-pipeline" badge={t.badge('pipelineTypeID', 'Pipeline Type')}>
          <Select<number>
            id="plan-type-pipeline"
            options={pipelineOptions}
            value={t.form.pipelineTypeID}
            onChange={(v) => t.patch({ pipelineTypeID: v ?? 0 })}
          />
        </DialogField>
        <SwitchField
          id="plan-type-restrict-quote"
          label="Restrict to One Active Quote"
          checked={t.form.restrictActiveQuote}
          onChange={(v) => t.patch({ restrictActiveQuote: v })}
          badge={t.badge('restrictActiveQuote', 'Restrict to One Active Quote')}
        />
      </div>
    </AdminFormModal>
  );
}
